import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, GripVertical } from "lucide-react";
import { addDays, startOfWeek, format } from "date-fns";
import { it } from "date-fns/locale";
import { api, errMsg } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { isoDate, SLOTS } from "@/lib/format";
import { PageHeader } from "@/components/Common";
import { ScheduleDialog } from "@/components/ScheduleDialog";
import { OrdersMap } from "@/components/OrdersMap";
import { useScheduleConfirm } from "@/components/ScheduleConfirm";
import { useAvailability } from "@/components/ShiftsPanel";
import { PriorityBadge, SlaChip, StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const OrderChip = ({ o, onClick, compact }) => (
  <div draggable onDragStart={(e) => e.dataTransfer.setData("text/plain", o.id)} onClick={() => onClick(o)} data-testid={`plan-order-${o.external_order_id}`}
    className={`group cursor-grab border bg-white p-2 text-left transition-colors hover:border-primary active:cursor-grabbing border-l-[3px] ${o.service_type === "ASSURANCE" ? "border-l-urgent" : "border-l-primary"}`}>
    <div className="flex min-w-0 items-center gap-1">
      <GripVertical className="h-3 w-3 shrink-0 text-muted-foreground" />
      <span className="truncate font-mono text-xs font-semibold">{o.external_order_id}</span>
      {o.delivery?.appointment_time && <span className="ml-auto shrink-0 bg-primary/10 px-1 font-mono text-[10px] font-semibold text-primary" data-testid={`plan-order-time-${o.external_order_id}`}>{o.delivery.appointment_time.slice(0, 5)}</span>}
    </div>
    {o.slot && compact && <div className="text-[10px] uppercase text-muted-foreground">{SLOTS[o.slot]}</div>}
    <div className="mt-1 truncate text-[11px] text-muted-foreground">{o.address?.city} · {o.address?.street}</div>
    {!compact && <div className="mt-1.5 flex flex-wrap gap-1"><PriorityBadge priority={o.priority} /><SlaChip order={o} /></div>}
    {compact && o.status !== "PLANNED" && <div className="mt-1"><StatusBadge status={o.status} /></div>}
  </div>
);

const Backlog = ({ orders, onPick, onDropBack }) => {
  const [type, setType] = useState("all");
  const list = orders.filter((o) => type === "all" || o.service_type === type)
    .sort((a, b) => (b.service_type === "ASSURANCE") - (a.service_type === "ASSURANCE") || (a.sla_due_at || "z").localeCompare(b.sla_due_at || "z"));
  return (
    <div className="flex max-h-[calc(100vh-220px)] flex-col border bg-white" data-testid="planning-backlog"
      onDragOver={(e) => e.preventDefault()} onDrop={(e) => onDropBack(e.dataTransfer.getData("text/plain"))}>
      <div className="flex items-center justify-between border-b px-4 py-3">
        <span className="micro-label">Da pianificare ({list.length})</span>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="h-8 w-32" data-testid="planning-backlog-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tutti</SelectItem><SelectItem value="ASSURANCE">Assurance</SelectItem><SelectItem value="DELIVERY">Delivery</SelectItem><SelectItem value="CONSTRUCTION">Construction</SelectItem></SelectContent>
        </Select>
      </div>
      <div className="flex-1 space-y-2 overflow-y-auto p-3">
        {list.length === 0 && <div className="p-4 text-center text-sm text-muted-foreground">Nessun ordine da pianificare</div>}
        {list.map((o) => <OrderChip key={o.id} o={o} onClick={onPick} />)}
      </div>
    </div>
  );
};

const Cell = ({ teamId, day, orders, notes, onDrop, onPick, outOfLoan }) => {
  const [over, setOver] = useState(false);
  if (outOfLoan) {
    return (
      <div className="flex min-h-[110px] items-center justify-center border-b border-r bg-[repeating-linear-gradient(135deg,#e5e7eb_0,#e5e7eb_6px,#f3f4f6_6px,#f3f4f6_12px)] text-[10px] font-semibold uppercase tracking-wider text-slate-500"
        title="Fuori dal periodo di prestito: tecnico non assegnabile" data-testid={`plan-cell-${teamId}-${day}-out-of-loan`}
        onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); toast.error("Fuori dal periodo di prestito: il tecnico non è assegnabile in questo giorno"); }}>
        Fuori prestito
      </div>
    );
  }
  return (
    <div className="relative border-b border-r">
    {notes.length > 0 && (
      <div className="space-y-0.5 px-1.5 pt-1.5" data-testid={`plan-avail-${teamId}-${day}`}>
        {notes.map((n) => <div key={n.text} className={`truncate px-1 text-[10px] font-semibold ${n.blocking ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-900"}`} title={n.text}>{n.text}</div>)}
      </div>
    )}
    <div data-testid={`plan-cell-${teamId}-${day}`}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); onDrop(e.dataTransfer.getData("text/plain"), teamId, day); }}
      className={`max-h-[260px] min-h-[110px] space-y-1.5 overflow-y-auto p-1.5 transition-colors ${over ? "drop-target" : ""}`}>
      {orders.map((o) => <OrderChip key={o.id} o={o} onClick={onPick} compact />)}
    </div>
    {orders.length > 3 && <div className="pointer-events-none absolute bottom-0 right-1 bg-[#0B0F14] px-1.5 font-mono text-[10px] text-white">{orders.length}</div>}
    </div>
  );
};

export default function Planning() {
  const { teams, companyId, maps, parties, projects, visibleCompanies } = useData();
  const [region, setRegion] = useState("all");
  const [customer, setCustomer] = useState("all");
  const regions = [...new Set(projects.flatMap((p) => p.regions))];
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [planned, setPlanned] = useState([]);
  const [backlog, setBacklog] = useState([]);
  const [view, setView] = useState("board");
  const [picked, setPicked] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const visibleTeams = teams.filter((t) => !companyId || t.company_id === companyId || t.loan?.to_company_id === companyId);
  const borrowed = (t) => t.loan && (companyId ? companyId === t.loan.to_company_id && t.company_id !== companyId : !visibleCompanies.some((c) => c.id === t.company_id));
  const outOfLoan = (t, day) => borrowed(t) && (day < t.loan.date_from || day > t.loan.date_to);
  const [avail, reloadAvail] = useAvailability(isoDate(days[0]), isoDate(days[6]));
  const [schedule, confirmDialog] = useScheduleConfirm();
  const notesFor = (t, day) => t.member_ids.map((id) => {
    const entry = avail[`${id}|${day}`];
    const e = maps.employees?.[id];
    return entry && e ? { text: `${e.first_name}: ${entry.label}`, blocking: entry.kind === "ABSENCE" && !entry.pending } : null;
  }).filter(Boolean);

  const load = useCallback(() => {
    const cp = { company_id: companyId || undefined, region: region === "all" ? undefined : region, customer_id: customer === "all" ? undefined : customer };
    api.get("/orders", { params: { ...cp, date_from: isoDate(days[0]), date_to: isoDate(days[6]), status: "PLANNED,IN_PROGRESS,COMPLETED,TO_VALIDATE" } }).then((r) => setPlanned(r.data)).catch(() => {});
    api.get("/orders", { params: { ...cp, unscheduled: true } }).then((r) => setBacklog(r.data)).catch(() => {});
    reloadAvail();
  }, [companyId, days, region, customer, reloadAvail]);
  useEffect(() => { load(); }, [load]);

  const all = useMemo(() => Object.fromEntries([...planned, ...backlog].map((o) => [o.id, o])), [planned, backlog]);

  const onDrop = async (orderId, teamId, day) => {
    const o = all[orderId];
    if (!o) return;
    const team = maps.teams[teamId];
    try {
      const r = await schedule(orderId, { team_id: teamId, employee_id: o.team_id === teamId ? o.employee_id : null, scheduled_date: day, slot: o.slot || "mattina" });
      if (!r) return;
      toast.success(`${o.external_order_id} → ${team.name}, ${format(new Date(`${day}T00:00:00`), "EEE d MMM", { locale: it })}`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const onDropBack = async (orderId) => {
    if (!all[orderId] || all[orderId].status !== "PLANNED") return;
    await api.post(`/orders/${orderId}/unschedule`).then(load).catch((e) => toast.error(errMsg(e)));
  };

  const pick = (o) => { setDefaults(null); setPicked(o); };
  const todayIso = isoDate(new Date());

  return (
    <div data-testid="planning-page">
      <PageHeader eyebrow="Schermata C" title="Pianificazione">
        <Button variant="outline" size="icon" onClick={() => setWeekStart(addDays(weekStart, -7))} data-testid="planning-prev-week"><ChevronLeft className="h-4 w-4" /></Button>
        <div className="min-w-[200px] text-center font-mono text-sm" data-testid="planning-week-label">{format(days[0], "d MMM", { locale: it })} — {format(days[6], "d MMM yyyy", { locale: it })}</div>
        <Button variant="outline" size="icon" onClick={() => setWeekStart(addDays(weekStart, 7))} data-testid="planning-next-week"><ChevronRight className="h-4 w-4" /></Button>
        <Button variant="outline" onClick={() => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))} data-testid="planning-today">Oggi</Button>
        <Select value={region} onValueChange={setRegion}>
          <SelectTrigger className="w-40 bg-white" data-testid="planning-region-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tutte le regioni</SelectItem>{regions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={customer} onValueChange={setCustomer}>
          <SelectTrigger className="w-44 bg-white" data-testid="planning-customer-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tutti i committenti</SelectItem>{parties.filter((p) => p.roles.includes("customer")).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
        </Select>
        <div className="flex border" data-testid="planning-view-toggle">
          <Button size="sm" variant={view === "board" ? "default" : "ghost"} onClick={() => setView("board")} data-testid="planning-view-board">Tabellone</Button>
          <Button size="sm" variant={view === "map" ? "default" : "ghost"} onClick={() => setView("map")} data-testid="planning-view-map">Mappa</Button>
        </div>
      </PageHeader>
      {view === "map" ? <OrdersMap params={{ company_id: companyId || undefined, region: region === "all" ? undefined : region, customer_id: customer === "all" ? undefined : customer }} onPick={pick} /> : <>
      <p className="-mt-4 mb-6 text-sm text-muted-foreground">Trascina un ordine su una cella squadra/giorno per assegnarlo, oppure cliccalo per scegliere tecnico e fascia. Trascinalo nella colonna di sinistra per rimuovere l'assegnazione.</p>
      <div className="grid gap-4 xl:grid-cols-[300px_1fr]">
        <Backlog orders={backlog} onPick={pick} onDropBack={onDropBack} />
        <div className="overflow-x-auto border-l border-t bg-white" data-testid="planning-grid">
          <div className="grid min-w-[1000px]" style={{ gridTemplateColumns: "180px repeat(7, minmax(120px, 1fr))" }}>
            <div className="border-b border-r bg-muted/50 px-3 py-2 micro-label">Squadra</div>
            {days.map((d) => (
              <div key={d.toISOString()} className={`border-b border-r px-3 py-2 ${isoDate(d) === todayIso ? "bg-[#0B0F14] text-white" : "bg-muted/50"}`}>
                <div className="text-[10px] uppercase tracking-[0.18em] opacity-70">{format(d, "EEEE", { locale: it })}</div>
                <div className="font-mono text-sm font-semibold">{format(d, "dd/MM")}</div>
              </div>
            ))}
            {visibleTeams.map((t) => (
              <div key={t.id} className="contents">
                <div className="border-b border-r px-3 py-3">
                  <div className="text-sm font-semibold">{t.name}</div>
                  {t.loan && <div className="mt-0.5 inline-block bg-amber-100 px-1.5 text-[10px] font-semibold text-amber-800" data-testid={`planning-loan-${t.id}`}>In prestito a {maps.companies?.[t.loan.to_company_id]?.code} {t.loan.date_from.slice(8, 10)}/{t.loan.date_from.slice(5, 7)}–{t.loan.date_to.slice(8, 10)}/{t.loan.date_to.slice(5, 7)}</div>}
                  <div className="text-xs text-muted-foreground">{maps.companies?.[t.company_id]?.code} · {t.discipline}</div>
                  <div className="mt-1 text-[11px] text-muted-foreground">{t.member_ids.map((id) => { const e = maps.employees?.[id]; return e ? `${e.first_name}${e.company_id !== t.company_id ? ` (${maps.companies?.[e.company_id]?.code})` : ""}` : null; }).filter(Boolean).join(", ")}</div>
                </div>
                {days.map((d) => {
                  const day = isoDate(d);
                  const items = planned.filter((o) => o.team_id === t.id && o.scheduled_date === day).sort((a, b) => (a.slot || "").localeCompare(b.slot || ""));
                  return <Cell key={day} teamId={t.id} day={day} orders={items} notes={notesFor(t, day)} onDrop={onDrop} onPick={pick} outOfLoan={outOfLoan(t, day) && !items.length} />;
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
      </>}
      <ScheduleDialog open={!!picked} onOpenChange={(o) => !o && setPicked(null)} order={picked} defaults={defaults} onDone={load} />
      {confirmDialog}
    </div>
  );
}
